import { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { BarChart3, LogOut, Sun, Moon, User, Lock, ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../contexts/ThemeContext'
import YearSelector from './YearSelector'
import VehicleFilterToggle from './VehicleFilterToggle'
import NotificationBell from './NotificationBell'
import LanguageSwitcher from './LanguageSwitcher'
import KlasseToggle from '../pages/AndreKjoretoy/KlasseToggle'

export default function Header() {
  const { t } = useTranslation('nav')
  const { user, logout } = useAuth()
  const { isDark, toggleTheme } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const hideYearSelector = ['/total-statistics', '/age-analytics', '/organizations'].some(
    p => location.pathname.startsWith(p)
  )
  // Leads runs its own vehicle filter (#335), decoupled from
  // VehicleFilterContext — nothing under src/pages/Leads reads the global one.
  // Leaving this toggle up there gave the module two controls for the same
  // idea, one of them inert: clicking it did nothing, and it could sit on
  // "Varebil" while the module showed personbiler.
  //
  // Ståtid is the same story (#523): it has its own filter set (backend #274)
  // and StaatidFilters has no varebil field at all, so the toggle never
  // reached the query — it just sat there looking like it filtered something.
  //
  // Kampanjer is the same (#583): neither the leasing nor the loan grid reads
  // VehicleFilterContext. The exclusion moved from /leasingkampanjer to the
  // /kampanjer prefix when the page became two tabs; the old path is kept
  // because it still resolves here before its redirect fires.
  const hideVehicleFilter =
    location.pathname.startsWith('/kampanjer') ||
    location.pathname === '/leasingkampanjer' ||
    location.pathname.startsWith('/leads') ||
    location.pathname.startsWith('/bruktbil/staatid')
  const segmentPage = location.pathname === '/segment-analytics'
  // Andre kjøretøy (#982) has no personbil/varebil: its classes take the toggle's place.
  const andreKjoretoyPage = location.pathname.startsWith('/andre-kjoretoy')

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  return (
    <header className="bg-white dark:bg-dark-surface border-b border-gray-200 dark:border-dark-border sticky top-0 z-40 transition-colors duration-200">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center space-x-2">
            <BarChart3 className="w-8 h-8 text-primary-500" />
            <span className="text-xl font-bold text-gray-900 dark:text-gray-100">
              bilpriser.no <span className="text-primary-500">PROFF</span>
            </span>
          </Link>

          {/* Right side */}
          <div className="flex items-center space-x-4">
            {/* Vehicle Type Filter */}
            {andreKjoretoyPage && <KlasseToggle />}
            {!hideVehicleFilter && !andreKjoretoyPage && (
              <VehicleFilterToggle
                disabledOptions={segmentPage ? ['varebil', 'alle'] : undefined}
                disabledTooltip={segmentPage ? t('header.segmentsPersonbilOnly') : undefined}
              />
            )}
            
            {/* Year Selector */}
            {!hideYearSelector && <YearSelector />}
            
            <LanguageSwitcher />

            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-card transition-colors"
              aria-label={t('header.toggleTheme')}
            >
              {isDark ? (
                <Sun className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              ) : (
                <Moon className="w-5 h-5 text-gray-600" />
              )}
            </button>

            {/* Notification bell (hidden when user has no watched orgs) */}
            {user && <NotificationBell />}

            {/* User menu */}
            {user && (
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {user.username || user.email}
                  </div>
                  <User className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                  <ChevronDown className={`h-4 w-4 text-gray-600 dark:text-gray-400 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {userMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} />
                    <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-20">
                      <div className="py-1">
                        <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {user.username || user.email}
                          </p>
                          {user.email && user.username && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {user.email}
                            </p>
                          )}
                        </div>
                        
                        <button
                          onClick={() => {
                            setUserMenuOpen(false)
                            navigate('/change-password')
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                        >
                          <Lock className="h-4 w-4" />
                          {t('header.changePassword')}
                        </button>
                        
                        <button
                          onClick={() => {
                            setUserMenuOpen(false)
                            handleLogout()
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                        >
                          <LogOut className="h-4 w-4" />
                          {t('header.logout')}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}
